import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
	UserPlus,
	AlertCircle,
	KeyRound,
	Mail,
	User,
	Loader2,
	ArrowLeft,
} from "lucide-react";
import { register, clearError } from "../actions/userActions";
import { FormInput } from "../components/FormInput";

const ErrorMessage = ({ message }) => (
	<div className="bg-red-50 border border-red-200 text-red-600 rounded-lg p-4 flex items-start gap-3">
		<AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
		<p>{message}</p>
	</div>
);

const RegisterScreen = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const [formData, setFormData] = useState({
		name: "",
		email: "",
		password: "",
		confirmPassword: "",
	});

	const [formErrors, setFormErrors] = useState({
		password: "",
		confirmPassword: "",
	});

	const userRegister = useSelector((state) => state.userRegister);
	const { loading, error, userInfo } = userRegister;

	const [searchParams] = useSearchParams();
	const redirectPath = searchParams.get("redirect");

	useEffect(() => {
		dispatch(clearError());
	}, [dispatch]);

	useEffect(() => {
		if (userInfo) {
			if (redirectPath) {
				navigate(redirectPath);
			} else {
				navigate("/");
			}
		}
	}, [navigate, userInfo, redirectPath]);

	const validatePassword = (password) => {
		if (password.length < 6 || !password.match("[0-9]")) {
			return "Password must be at least 6 characters and contain numbers";
		}
		return "";
	};

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));

		// Password validation
		if (name === "password") {
			const error = validatePassword(value);
			setFormErrors((prev) => ({
				...prev,
				password: error,
				confirmPassword:
					value !== formData.confirmPassword ? "Passwords must match" : "",
			}));
		}

		// Confirm password validation
		if (name === "confirmPassword") {
			setFormErrors((prev) => ({
				...prev,
				confirmPassword:
					value !== formData.password ? "Passwords must match" : "",
			}));
		}
	};

	const handleSubmit = (e) => {
		e.preventDefault();

		if (!formErrors.password && !formErrors.confirmPassword) {
			dispatch(register(formData.name, formData.email, formData.password));
		}
	};

	const error401 = error?.includes("401");

	return (
		<div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
			<div className="max-w-md mx-auto">
				<div className="text-center mb-8">
					<h2 className="text-3xl font-bold text-gray-900">
						Create an account
					</h2>
					<p className="mt-2 text-gray-600">Join us to get started</p>
				</div>

				<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
					{error401 ? (
						<ErrorMessage message="The email may be registered, please try to reset your password." />
					) : error ? (
						<ErrorMessage message={error} />
					) : null}

					<form onSubmit={handleSubmit} className="space-y-6 mt-6">
						<FormInput
							label="Full Name"
							name="name"
							type="text"
							icon={User}
							required
							value={formData.name}
							onChange={handleInputChange}
							placeholder="Enter your full name"
						/>

						<FormInput
							label="Email Address"
							name="email"
							type="email"
							icon={Mail}
							required
							value={formData.email}
							onChange={handleInputChange}
							placeholder="Enter your email"
						/>

						<FormInput
							label="Password"
							name="password"
							type="password"
							icon={KeyRound}
							required
							value={formData.password}
							onChange={handleInputChange}
							error={formErrors.password}
							placeholder="Create a password"
						/>

						<FormInput
							label="Confirm Password"
							name="confirmPassword"
							type="password"
							icon={KeyRound}
							required
							value={formData.confirmPassword}
							onChange={handleInputChange}
							error={formErrors.confirmPassword}
							placeholder="Confirm your password"
						/>

						<button
							type="submit"
							disabled={
								loading || Object.values(formErrors).some((error) => error)
							}
							className="w-full px-4 py-3 bg-primary text-white font-medium rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
						>
							{loading ? (
								<>
									<Loader2 className="w-5 h-5 animate-spin" />
									Creating account...
								</>
							) : (
								<>
									<UserPlus className="w-5 h-5" />
									Create Account
								</>
							)}
						</button>
					</form>

					<div className="mt-8 pt-6 border-t border-gray-200 text-center">
						<p className="text-gray-600">Already have an account?</p>
						<Link
							to={redirectPath ? `/login?redirect=${redirectPath}` : "/login"}
							className="mt-4 w-full inline-flex items-center justify-center px-4 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors gap-2"
						>
							<ArrowLeft className="w-5 h-5" />
							Back to Sign In
						</Link>
					</div>
				</div>
			</div>

			{/* Loading Overlay */}
			<AnimatePresence>
				{loading && (
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className="fixed inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-50"
					>
						<div className="flex flex-col items-center gap-4">
							<div className="relative w-32 h-32">
								<img
									src="/images/logoDark.png"
									alt="Logo"
									className="w-full h-full object-contain"
								/>
								<div className="absolute inset-0 border-t-4 border-primary rounded-full animate-spin" />
							</div>
							<p className="text-lg font-medium text-gray-900">
								Creating your account...
							</p>
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	);
};

export default RegisterScreen;
